import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  Row,
  Col,
  Typography,
  Table,
  Tag,
  Button,
  Card,
  Select,
  DatePicker,
  Space,
  Modal,
  Progress,
  message,
  Divider,
  Radio,
  Alert,
  Tooltip,
  Tabs,
} from 'antd';
import {
  FileTextOutlined,
  DownloadOutlined,
  SyncOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FilePdfOutlined,
  CalendarOutlined,
  FileSearchOutlined,
  FileAddOutlined,
  InfoCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { DashboardLayout } from '../components/layout';
import CyberEssentialsReport from '../components/widgets/CyberEssentialsReport';
import MfaClassificationReport from '../components/widgets/MfaClassificationReport';
import DirectorySyncReport from '../components/widgets/DirectorySyncReport';
import SophosEmailSyncReport from '../components/widgets/SophosEmailSyncReport';
import { DataCard } from '../components/common';
import { useTenant } from '../context/TenantContext';
import { useMCPMutation, useMCPQuery, queryKeys } from '../hooks';
import {
  generateUnifiedMSRFromTenant,
  generateCeReadinessReport,
  resolveOdinCustomerId,
  buildPdfDownloadUrl,
  downloadReportPdf,
} from '../services/api/odinApi';
import { isM365Managed } from '../utils/customerKey';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

const PageHeader = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

// Table chrome comes from the root ConfigProvider (theme spec section 2.4): no page-local .ant-* overrides.
const StyledTable = styled(Table)``;

const GenerateCard = styled(Card)``;

const ReportTypeCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s ease;

  &.disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  &:hover {
    border-color: ${({ theme }) => theme.colors.accent};
  }

  &.selected {
    border-color: ${({ theme }) => theme.colors.accent};
    background: color-mix(in srgb, ${({ theme }) => theme.colors.accent} 6%, transparent);
  }
`;

const ReportIcon = styled.div`
  font-size: 48px;
  color: ${({ theme }) => theme.colors.accent};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const getStatusConfig = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return { color: 'success', icon: <CheckCircleOutlined />, text: 'Completed' };
    case 'generating':
    case 'in_progress':
      return { color: 'processing', icon: <SyncOutlined spin />, text: 'Generating' };
    case 'failed':
      return { color: 'error', icon: <ExclamationCircleOutlined />, text: 'Failed' };
    case 'pending':
      return { color: 'warning', icon: <ClockCircleOutlined />, text: 'Pending' };
    default:
      return { color: 'default', icon: null, text: status || 'Unknown' };
  }
};

const ReportsPage = () => {
  const navigate = useNavigate();
  const { selectedTenant, tenants } = useTenant();
  const [selectedReportType, setSelectedReportType] = useState('msr');
  const [dateRange, setDateRange] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [outputFormat, setOutputFormat] = useState('pdf'); // 'json' or 'pdf'
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStatus, setGenerationStatus] = useState('');
  const [lastGeneratedReport, setLastGeneratedReport] = useState(null);

  // CE Readiness needs a CIPP tenant: Odin-MCP refuses it without one (spec 2026-10-19)
  const ceUnavailable = !!selectedTenant && !isM365Managed(selectedTenant);
  useEffect(() => {
    if (ceUnavailable) setSelectedReportType('msr');
  }, [ceUnavailable]);

  // Fetch existing reports
  // NOTE: list_reports tool does not exist in Odin-MCP yet - disabled to prevent circuit breaker trips
  const { data: reports, isLoading: reportsLoading, refetch: refetchReports } = useMCPQuery({
    server: 'Odin-MCP',
    tool: 'list_reports',
    args: { customer_id: selectedTenant?.id },
    queryKey: queryKeys.odin.reports(selectedTenant?.id),
    enabled: false, // Disabled - tool not implemented yet
  });

  /**
   * Generate MSR report using the unified MSR tool via Odin-MCP
   * Handles customer ID resolution, async generation, and format selection
   */
  const handleGenerateReport = useCallback(async () => {
    // Validate that a customer is selected on the dashboard
    if (!selectedTenant) {
      message.warning('Please select a customer from the dashboard first');
      return;
    }

    setGeneratingReport(true);
    setGenerationProgress(10);
    setGenerationStatus('Resolving customer mapping...');

    try {
      // Step 1: Resolve customer ID
      setGenerationProgress(20);
      setGenerationStatus('Connecting to Odin-MCP...');

      // Build report period if date range is selected
      const reportPeriod = dateRange ? {
        start: dateRange[0].toISOString(),
        end: dateRange[1].toISOString(),
      } : undefined;

      // Get customer display name from selected tenant
      const customerName = selectedTenant.displayName || selectedTenant.name;

      setGenerationProgress(40);
      setGenerationStatus('Generating report...');

      let result;

      if (selectedReportType === 'ce_readiness' && ceUnavailable) {
        message.warning('CE Readiness needs a managed Microsoft 365 tenant.');
        return; // exits the try block — finally still runs
      } else if (selectedReportType === 'ce_readiness') {
        // CE Readiness Report — fetch from DB + Puppeteer PDF
        setGenerationStatus('Generating CE Readiness Report...');
        const ceResult = await generateCeReadinessReport(selectedTenant.id, { customerName });

        if (!ceResult?.pdfId) {
          // success: false returned by tool — show specific message
          const errMsg = ceResult?.error || 'No CE scan data available for this customer.';
          message.warning({
            content: errMsg,
            duration: 8,
          });
          return; // exits the try block — finally still runs
        }

        // Map CE result shape to the same shape as MSR for the download flow below
        result = {
          pdfId: ceResult.pdfId,
          reportId: null,
          healthScore: null,
          dataSourcesConnected: 1,
          recommendationCount: 0,
          _ceOverallStatus: ceResult.overallStatus,
          _ceGapCount: ceResult.gapCount,
        };
      } else {
        // Standard MSR
        setGenerationStatus(outputFormat === 'pdf' ? 'Generating PDF report...' : 'Collecting report data...');
        result = await generateUnifiedMSRFromTenant(selectedTenant.id, {
          customerName,
          outputFormat,
          reportPeriod,
        });
      }

      setGenerationProgress(80);
      setGenerationStatus('Processing results...');

      // Handle the response based on format
      if (outputFormat === 'pdf' || selectedReportType === 'ce_readiness') {
        // PDF format — MSR or CE Readiness both return a pdfId
        if (result?.pdfId) {
          const downloadUrl = buildPdfDownloadUrl(result.pdfId);

          setLastGeneratedReport({
            id: result.pdfId,
            reportId: result.reportId || null,
            type: selectedReportType === 'ce_readiness' ? 'ce_readiness' : 'msr',
            format: 'pdf',
            customerName: customerName || selectedTenant.id,
            downloadUrl,
            healthScore: result.healthScores?.overall || result.healthScore,
            dataSourcesConnected: result.dataSourcesConnected || 0,
            recommendationCount: result.recommendationCount || 0,
            createdAt: new Date().toISOString(),
            status: 'completed',
          });

          message.success({
            content: 'PDF report generated and downloaded successfully!',
            duration: 5,
          });

          // Auto-download the PDF (extracts base64 from HTML response)
          const downloadResult = await downloadReportPdf(
            result.pdfId,
            `${selectedReportType === 'ce_readiness' ? 'CE' : 'MSR'}_${customerName || selectedTenant.id}_${new Date().toISOString().split('T')[0]}.pdf`
          );
          if (!downloadResult.success) {
            console.warn('[ReportsPage] Auto-download failed:', downloadResult.error);
            message.warning('Auto-download failed. Use the download button to try again.');
          }
        } else {
          throw new Error('PDF generation did not return a pdfId');
        }
      } else {
        // JSON format - we get the full report data
        setLastGeneratedReport({
          id: `json-${Date.now()}`,
          type: 'msr',
          format: 'json',
          customerName: customerName || selectedTenant.id,
          data: result,
          healthScore: result?.healthScores?.overall || 0,
          dataSourcesConnected: result?.dataSourceStatus?.filter(s => s.connected)?.length || 0,
          recommendationCount: result?.recommendations?.length || 0,
          createdAt: new Date().toISOString(),
          status: 'completed',
        });

        message.success('JSON report data generated successfully!');

        // Show a modal or download the JSON
        Modal.info({
          title: 'MSR Report Data Generated',
          width: 800,
          content: (
            <div style={{ maxHeight: '60vh', overflow: 'auto' }}>
              <Alert
                type="success"
                message={`Health Score: ${result?.healthScores?.overall || 'N/A'}%`}
                description={`${result?.recommendations?.length || 0} recommendations generated`}
                style={{ marginBottom: 16 }}
              />
              <pre style={{ fontSize: 12, background: 'var(--odin-code-bg)', padding: 16, borderRadius: 8 }}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          ),
          okText: 'Download JSON',
          onOk: () => {
            // Download the JSON file
            const blob = new Blob([JSON.stringify(result, null, 2)], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `MSR_${customerName || selectedTenant.id}_${new Date().toISOString().split('T')[0]}.json`;
            a.click();
            URL.revokeObjectURL(url);
          },
        });
      }

      setGenerationProgress(100);
      setGenerationStatus('Complete!');
      // NOTE: refetchReports() removed - list_reports tool does not exist in Odin-MCP yet
      // Calling it trips the circuit breaker (400 → counted as failure → breaker opens)
      // which blocks subsequent Odin-MCP calls (recommendations, templates, etc.)

    } catch (error) {
      console.error('[ReportsPage] Report generation failed:', error);

      if (error.message?.includes('Could not resolve customer')) {
        message.error({
          content: 'This customer has not been mapped to platform IDs yet. Please use Customer Discovery on the Dashboard.',
          duration: 5,
        });
      } else {
        message.error(`Failed to generate report: ${error.message}`);
      }

      setGenerationStatus('Failed');
    } finally {
      setGeneratingReport(false);
      // Reset progress after a delay
      setTimeout(() => {
        setGenerationProgress(0);
        setGenerationStatus('');
      }, 2000);
    }
  }, [selectedTenant, dateRange, outputFormat, selectedReportType, refetchReports, ceUnavailable]);

  const handleDownload = async (report) => {
    if (report.downloadUrl && report.id) {
      const result = await downloadReportPdf(
        report.id,
        `${report.type === 'ce_readiness' ? 'CE' : 'MSR'}_${report.customerName || 'report'}_${new Date(report.createdAt).toISOString().split('T')[0]}.pdf`
      );
      if (!result.success) {
        message.error(result.error || 'Failed to download PDF');
      }
    } else if (report.format === 'json' && report.data) {
      // Download JSON data
      const blob = new Blob([JSON.stringify(report.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `MSR_${report.customerName}_${new Date(report.createdAt).toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } else {
      message.info('Report download not available yet');
    }
  };

  // Report table columns
  const reportColumns = [
    {
      title: 'Report Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          {record.format === 'json' ? (
            <FileSearchOutlined style={{ color: 'var(--odin-accent)' }} />
          ) : (
            <FilePdfOutlined style={{ color: 'var(--odin-error)' }} />
          )}
          <span>{name || `${record.type?.toUpperCase()} Report`}</span>
        </Space>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type) => {
        const label = type === 'ce_readiness' ? 'CE READY' : type?.toUpperCase() || 'MSR';
        const color = type === 'ce_readiness' ? 'success' : 'default';
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: 'Format',
      dataIndex: 'format',
      key: 'format',
      width: 80,
      render: (format) => (
        <Tag color={format === 'pdf' ? 'default' : 'processing'}>
          {format?.toUpperCase() || 'PDF'}
        </Tag>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customerName',
      key: 'customer',
      ellipsis: true,
    },
    {
      title: 'Health',
      dataIndex: 'healthScore',
      key: 'health',
      width: 80,
      render: (score) => score ? (
        <Tag color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'error'}>
          {score}%
        </Tag>
      ) : '--',
    },
    {
      title: 'Generated',
      dataIndex: 'createdAt',
      key: 'created',
      width: 160,
      render: (date) => date ? new Date(date).toLocaleString() : '--',
      sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const { color, icon, text } = getStatusConfig(status);
        return <Tag color={color} icon={icon}>{text}</Tag>;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleDownload(record)}
            disabled={record.status !== 'completed'}
          >
            {record.format === 'json' ? 'View' : 'Download'}
          </Button>
          {record.reportId && record.format === 'pdf' && (
            <Tooltip title="Add recommendation pages to this report">
              <Button
                size="small"
                icon={<FileAddOutlined />}
                onClick={() => navigate(
                  `/reports/recommendations?reportId=${record.reportId}&basePdfId=${record.id}&customer=${encodeURIComponent(record.customerName || '')}`
                )}
              >
                Recs
              </Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const reportTypes = [
    {
      key: 'msr',
      title: 'Monthly Service Report',
      description: 'Comprehensive monthly report with metrics from all platforms',
      icon: <FileTextOutlined />,
    },
    {
      key: 'ce_readiness',
      title: 'CE Readiness Report',
      description: 'Cyber Essentials readiness assessment with gap analysis and remediation steps',
      icon: <SafetyCertificateOutlined />,
      disabled: ceUnavailable,
      disabledReason: 'Needs a managed Microsoft 365 tenant',
    },
  ];

  return (
    <DashboardLayout>
      <PageHeader>
        <Title level={2} style={{ color: 'var(--odin-text-primary)', marginBottom: 8 }}>
          <FileTextOutlined style={{ marginRight: 12, color: 'var(--odin-accent)' }} />
          Reports
        </Title>
        <Text style={{ color: 'var(--odin-text-secondary)' }}>
          Generate and download MSR reports for your customers
        </Text>
      </PageHeader>

      <Tabs defaultActiveKey="msr">
        <TabPane tab="MSR Reports" key="msr">

      {/* Generate Report Section */}
      <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <GenerateCard title="Generate New Report">
            {/* Report Type Selection */}
            <Paragraph style={{ color: 'var(--odin-text-secondary)', marginBottom: 16 }}>
              Select report type:
            </Paragraph>
            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              {reportTypes.map((type) => (
                <Col xs={24} sm={12} key={type.key}>
                  <ReportTypeCard
                    style={{ height: '100%' }}
                    className={type.disabled ? 'disabled' : selectedReportType === type.key ? 'selected' : ''}
                    onClick={() => {
                      if (!type.disabled) setSelectedReportType(type.key);
                    }}
                    aria-disabled={type.disabled ? 'true' : undefined}
                    data-testid={`report-type-${type.key}`}
                    size="small"
                  >
                    <div style={{ textAlign: 'center' }}>
                      <ReportIcon>{type.icon}</ReportIcon>
                      <Title level={5} style={{ color: 'var(--odin-text-primary)', marginBottom: 4 }}>
                        {type.title}
                      </Title>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {type.disabled ? type.disabledReason : type.description}
                      </Text>
                    </div>
                  </ReportTypeCard>
                </Col>
              ))}
            </Row>

            <Divider style={{ borderColor: 'var(--odin-border)' }} />

            {/* No Customer Selected Warning */}
            {!selectedTenant && (
              <Alert
                message="Select a Customer First"
                description={
                  <span>
                    To generate a report, please select a customer from the{' '}
                    <a href="/" style={{ color: 'var(--odin-accent)' }}>Dashboard</a> page first.
                    The active customer will be shown below.
                  </span>
                }
                type="info"
                showIcon
                icon={<InfoCircleOutlined />}
                style={{
                  marginBottom: 24,
                }}
              />
            )}

            {/* Options */}
            <Row gutter={[16, 16]} align="bottom">
              <Col xs={24} sm={6}>
                <Text style={{ color: 'var(--odin-text-secondary)', display: 'block', marginBottom: 8 }}>
                  Customer
                </Text>
                <Card
                  size="small"
                  style={{
                    border: '1px solid var(--odin-accent)',
                  }}
                >
                  {selectedTenant ? (
                    <Space direction="vertical" size={0}>
                      <Text strong style={{ color: 'var(--odin-accent)' }}>
                        {selectedTenant.displayName || selectedTenant.name}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        Active customer from dashboard
                      </Text>
                    </Space>
                  ) : (
                    <Alert
                      message="No Customer Selected"
                      description="Please select a customer from the Dashboard page"
                      type="info"
                      showIcon
                      style={{ margin: 0, padding: '8px 12px' }}
                    />
                  )}
                </Card>
              </Col>
              <Col xs={24} sm={6}>
                <Text style={{ color: 'var(--odin-text-secondary)', display: 'block', marginBottom: 8 }}>
                  <CalendarOutlined style={{ marginRight: 4 }} />
                  Date Range (Optional)
                </Text>
                <RangePicker
                  style={{ width: '100%' }}
                  value={dateRange}
                  onChange={setDateRange}
                />
              </Col>
              {selectedReportType !== 'ce_readiness' && (
              <Col xs={24} sm={6}>
                <Text style={{ color: 'var(--odin-text-secondary)', display: 'block', marginBottom: 8 }}>
                  Output Format
                  <Tooltip title="PDF generates a downloadable report. JSON returns raw data for integration.">
                    <InfoCircleOutlined style={{ marginLeft: 4, cursor: 'pointer' }} />
                  </Tooltip>
                </Text>
                <Radio.Group
                  value={outputFormat}
                  onChange={(e) => setOutputFormat(e.target.value)}
                  optionType="button"
                  buttonStyle="solid"
                  style={{ width: '100%' }}
                >
                  <Radio.Button value="pdf" style={{ width: '50%', textAlign: 'center' }}>
                    <FilePdfOutlined /> PDF
                  </Radio.Button>
                  <Radio.Button value="json" style={{ width: '50%', textAlign: 'center' }}>
                    <FileSearchOutlined /> JSON
                  </Radio.Button>
                </Radio.Group>
              </Col>
              )}
              <Col xs={24} sm={6}>
                <Button
                  type="primary"
                  size="large"
                  icon={generatingReport ? <SyncOutlined spin /> : <FileTextOutlined />}
                  onClick={handleGenerateReport}
                  loading={generatingReport}
                  disabled={generatingReport || !selectedTenant}
                  block
                >
                  {generatingReport ? 'Generating...' : 'Generate Report'}
                </Button>
              </Col>
            </Row>

            {/* Generation Progress */}
            {generatingReport && (
              <div style={{ marginTop: 24 }}>
                <Progress
                  percent={generationProgress}
                  status={generationProgress === 100 ? 'success' : 'active'}
                  strokeColor={{
                    '0%': 'var(--odin-accent)',
                    '100%': 'var(--odin-success)',
                  }}
                />
                <Text style={{ color: 'var(--odin-text-secondary)', display: 'block', textAlign: 'center', marginTop: 8 }}>
                  {generationStatus}
                </Text>
              </div>
            )}

            {/* Last Generated Report Summary */}
            {lastGeneratedReport && !generatingReport && (
              <Alert
                style={{ marginTop: 24 }}
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
                message={`Report generated for ${lastGeneratedReport.customerName}`}
                description={
                  <Space direction="vertical" size="small">
                    <Text style={{ color: 'var(--odin-text-primary)' }}>
                      Health Score: <strong>{lastGeneratedReport.healthScore || 'N/A'}%</strong> |
                      Data Sources: <strong>{lastGeneratedReport.dataSourcesConnected}/5</strong> |
                      Recommendations: <strong>{lastGeneratedReport.recommendationCount}</strong>
                    </Text>
                    {lastGeneratedReport.downloadUrl && (
                      <Space>
                        <Button
                          type="link"
                          icon={<DownloadOutlined />}
                          onClick={async () => {
                            const result = await downloadReportPdf(
                              lastGeneratedReport.id,
                              `${lastGeneratedReport.type === 'ce_readiness' ? 'CE' : 'MSR'}_${lastGeneratedReport.customerName || 'report'}_${new Date(lastGeneratedReport.createdAt).toISOString().split('T')[0]}.pdf`
                            );
                            if (!result.success) {
                              message.error(result.error || 'Failed to download PDF');
                            }
                          }}
                          style={{ padding: 0 }}
                        >
                          Download {lastGeneratedReport.format?.toUpperCase()} Report
                        </Button>
                        {lastGeneratedReport.reportId && (
                          <Button
                            type="link"
                            icon={<FileAddOutlined />}
                            onClick={() => navigate(
                              `/reports/recommendations?reportId=${lastGeneratedReport.reportId}&basePdfId=${lastGeneratedReport.id}&customer=${encodeURIComponent(lastGeneratedReport.customerName || '')}`
                            )}
                            style={{ padding: 0 }}
                          >
                            Add Recommendations
                          </Button>
                        )}
                      </Space>
                    )}
                  </Space>
                }
              />
            )}
          </GenerateCard>
        </Col>
      </Row>

      {/* Previous Reports Table */}
      <Row gutter={[24, 24]}>
        <Col xs={24}>
          <DataCard
            title="Previous Reports"
            icon={FileTextOutlined}
            loading={reportsLoading}
            empty={!reports?.length}
            emptyText="No reports generated yet"
            onRefresh={refetchReports}
          >
            <StyledTable
              dataSource={reports || []}
              columns={reportColumns}
              rowKey="id"
              pagination={{ pageSize: 10, showSizeChanger: true }}
              size="small"
            />
          </DataCard>
        </Col>
      </Row>

        </TabPane>
        <TabPane tab="Cyber Essentials Readiness" key="ce">
          <CyberEssentialsReport />
        </TabPane>
        <TabPane tab="MFA Status" key="mfa">
          <MfaClassificationReport />
        </TabPane>
        <TabPane tab="Directory Sync" key="dirsync">
          <DirectorySyncReport />
        </TabPane>
        <TabPane tab="Sophos Email Sync" key="sophos-sync">
          <SophosEmailSyncReport />
        </TabPane>
      </Tabs>
    </DashboardLayout>
  );
};

export default ReportsPage;
